import { ReactNode } from 'react';
import { View } from 'primase';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';

// Components
import { UpcomingMaintenance } from 'features/results/components/upcoming-maintenance/upcoming-maintenance';
import { BannerProvider } from 'components/banner';
import ToastContainer from '../toast';
import SupportLink from '../support-link';

// Constants
import { maintenanceBannerWhitelist } from './constants';

// Helpers
import { useFeatureFlags } from '../../helpers/hooks';
import { useAccount } from '../../config/account';

// Styles
import { styles } from './styles';

const ImpersonationBanner = loadable(
  () =>
    import(
      '../../features/account/components/impersonation/impersonation-banner/impersonation-banner'
    ),
);

interface IPageLayoutProps {
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  headerClasses?: Record<string, unknown>;
  contentClasses?: Record<string, unknown>;
  footerClasses?: Record<string, unknown>;
}

const PageLayout = ({
  header = null,
  children,
  footer = null,
  headerClasses,
  contentClasses,
  footerClasses,
}: IPageLayoutProps) => {
  const { isAuthenticated, isImpersonating } = useAccount();

  const showImpersonationBanner = isAuthenticated() && isImpersonating();
  const { upcomingMaintenanceEnabled } = useFeatureFlags();
  const { pathname } = useLocation();
  const showMaintenanceBanner =
    upcomingMaintenanceEnabled && maintenanceBannerWhitelist.includes(pathname);

  return (
    <View classes={styles.container}>
      <header>
        <nav aria-label="Main">
          <View
            id="application-header"
            css={
              headerClasses
                ? { ...styles.header, ...headerClasses }
                : styles.header
            }
          >
            {header}
          </View>
        </nav>
      </header>
      <ToastContainer />
      {showImpersonationBanner && <ImpersonationBanner />}
      {showMaintenanceBanner ? <UpcomingMaintenance /> : null}
      <BannerProvider>
        <main>
          <View
            classes={
              contentClasses
                ? { ...styles.content, ...contentClasses }
                : styles.content
            }
          >
            {children}
          </View>
        </main>
      </BannerProvider>
      <footer>
        <View
          classes={
            footerClasses
              ? { ...styles.footer, ...footerClasses }
              : styles.footer
          }
        >
          <SupportLink />
          {footer}
        </View>
      </footer>
    </View>
  );
};

export default PageLayout;
